import type { DelimiterConfig } from "../helper/index.js";
import { addAttrs, createDelimiterChecker, getMatchingOpeningToken } from "../helper/index.js";
import type { AttrRule } from "./types.js";
import { defineAttrRule } from "./types.js";

export const createInlineRules = (options: DelimiterConfig): AttrRule[] => [
  /**
   * bla `click()`{.c} ![](img.png){.d}
   *
   * differs from 'inline attributes' as it does
   * not have a closing tag (nesting: -1)
   */
  defineAttrRule({
    name: "inline nesting self-close",
    tests: [
      {
        shift: 0,
        type: "inline",
        children: [
          {
            shift: -1,
            // oxlint-disable-next-line typescript/explicit-function-return-type
            type: (str) => str === "image" || str === "code_inline",
          },
          {
            shift: 0,
            type: "text",
            content: createDelimiterChecker(options, "start"),
          },
        ],
      },
    ],
    transform: (tokens, index, childIndex, range): void => {
      // oxlint-disable-next-line typescript/no-non-null-assertion
      const childTokens = tokens[index].children!;
      const token = childTokens[childIndex];
      const targetToken = childTokens[childIndex - 1];
      const attrsEndIndex = options.right.length + range[1];

      // Apply attributes to the target token
      addAttrs(targetToken, token.content, range, options.allowed);

      if (token.content.length === attrsEndIndex) childTokens.splice(childIndex, 1);
      else token.content = token.content.slice(attrsEndIndex);
    },
  }),

  /**
   * *emphasis*{.with attrs=1}
   */
  defineAttrRule({
    name: "inline attributes",
    tests: [
      {
        shift: 0,
        type: "inline",
        children: [
          {
            shift: -1,
            nesting: -1, // closing inline tag, </em>{.a}
          },
          {
            shift: 0,
            type: "text",
            content: createDelimiterChecker(options, "start"),
          },
        ],
      },
    ],
    transform: (tokens, index, childIndex, range): void => {
      // oxlint-disable-next-line typescript/no-non-null-assertion
      const childTokens = tokens[index].children!;
      const currentToken = childTokens[childIndex];
      const { content } = currentToken;
      const attrsEndIndex = options.right.length + range[1];

      // Find the corresponding opening token
      const openingToken = getMatchingOpeningToken(childTokens, childIndex - 1);

      // Apply attributes to the opening token
      addAttrs(openingToken, content, range, options.allowed);

      // Remove attribute syntax from content
      currentToken.content = content.slice(attrsEndIndex);
    },
  }),
];
